import type { CommunityFeedPostFragment } from 'generated/graphql';
import {
  MediaItemStatus,
  Post,
  PostMediaItem,
  Reaction,
  ReactionType,
  toAuthorId,
  toPostId,
} from './post.types';

export type ServerPost = CommunityFeedPostFragment & {
  author: { email?: string | null; marketing?: boolean | null };
};

type ServerMedia = ServerPost['media'][number];
type ServerReaction = ServerPost['reactions'][number];

const toMediaStatus = (raw: string | null | undefined): MediaItemStatus =>
  raw === 'processing' || raw === 'failed' ? raw : 'ready';

const toMediaItem = (raw: ServerMedia): PostMediaItem | null => {
  const status = toMediaStatus(raw.status);

  const isAudio = !!raw.audio || (raw.mimeType?.startsWith('audio') ?? false);
  if (isAudio) {
    if (!raw.audio) return null;
    const cdn = raw.cdn01 ?? '';
    return {
      type: 'audio',
      url: `${cdn}${raw.audio}`,
      thumbnailUrl: '',
      status,
    };
  }

  const isMux = raw.videoSourceType === 'mux';
  const isVideo = isMux || (raw.mimeType?.startsWith('video') ?? false);

  let url: string;
  let thumbnailUrl: string;
  if (isMux) {
    url = raw.videoUrl ?? '';
    thumbnailUrl = raw.videoThumbnailUrl ?? raw.image ?? '';
  } else {
    const cdn = raw.cdn01 ?? '';
    const source = isVideo ? raw.video : raw.image;
    url = source ? `${cdn}${source}` : '';
    thumbnailUrl = `${cdn}${raw.image ?? source ?? ''}`;
  }

  // A ready item with no usable source is dropped; processing/failed items are
  // kept so the strip can render their in-progress or error state.
  if (status === 'ready' && !url) return null;

  return { type: isVideo ? 'video' : 'image', url, thumbnailUrl, status };
};

const toReaction = (raw: ServerReaction): Reaction => ({
  type: raw.type,
  count: raw.count,
  isToggled: raw.isToggled,
});

export const toPost = (raw: ServerPost): Post => ({
  id: toPostId(raw.id),
  author: {
    id: toAuthorId(raw.author.id),
    name: raw.author.username,
    avatarUrl: raw.author.profilePicture,
    email: raw.author.email || null,
    marketing: raw.author.marketing ?? null,
    isOwn: (raw.author.badges ?? []).includes('own'),
    isCreator: (raw.author.badges ?? []).includes('creator'),
    canReceiveDm: raw.author.hasDirectMessagesAccess === true,
  },
  createdAt: new Date(raw.createdAt),
  content: raw.text,
  edited: raw.edited,
  status: raw.status === 'PROCESSING' ? 'processing' : 'published',
  media: raw.media
    .map(toMediaItem)
    .filter((item): item is PostMediaItem => Boolean(item)),
  reactions: raw.reactions
    .filter((reaction) => reaction.type !== ReactionType.Comment)
    .map(toReaction),
  commentCount:
    raw.reactions.find((reaction) => reaction.type === ReactionType.Comment)
      ?.count ?? 0,
  pinned: raw.pinned ?? false,
});
